import * as MUI from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ConfirmAndCancelButtons from "../../../../../../components/buttons/ConfirmAndCancelButtons";
import { WAREHOUSE_ITEM_SUBMIT } from "../../../../../../enums/ApiPaths";
import {
  WAREHOUSE_CATEGORIES,
  WAREHOUSE_ITEMS,
  WAREHOUSE_ITEMS_SUBMIT,
  WAREHOUSE_SUPPLIERS,
} from "../../../../../../enums/QueryKeys";
import useUser from "../../../../../../hooks/useUser";
import { mandatory } from "../../../../../../utils/Functions";
import useSubmit from "../../../../../master/species/hooks/useSubmit";
import SearchableAutocomplete from "../components/SearchableAutocomplete";
import { useGetWarehousePreferences } from "../hooks/useGetWarehousePreferences";
import useUpdateWarehouseItem from "../hooks/useUpdateWarehouseItem";
import { decimalRules, parseDecimal } from "../utils/decimalInput";
import { MEASURING_UNITS } from "../utils/measuringUnits";
import { formatCurrency } from "../utils/stockStatus";

export const QUANTITY_STEPS = [-10, -1, 1, 10];

export const DESCRIPTION_MAX_LENGTH = 500;
export const TITLE_MAX_LENGTH = 120;
export const CATEGORY_MAX_LENGTH = 120;
export const SUPPLIER_MAX_LENGTH = 120;
export const CODE_MAX_LENGTH = 60;
export const SERIAL_NUMBER_MAX_LENGTH = 60;

export const emptyValues = {
  title: "",
  description: "",
  category: "",
  supplier: "",
  price: "",
  pdv: "",
  quantity: "",
  code: "",
  serialNumber: "",
  measuringUnit: "",
  discount: "",
  antiRabiesVaccine: false,
  otherVaccine: false,
  narcoticDrug: false,
  prescriptionRequired: false,
  expirationDate: null,
};

function SectionCard({ label, children }) {
  return (
    <MUI.Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, borderColor: "divider" }}>
      <MUI.Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: "0.06em" }}
      >
        {label}
      </MUI.Typography>
      <MUI.Box sx={{ mt: 1.75 }}>{children}</MUI.Box>
    </MUI.Paper>
  );
}

// price/pdv/discount are optional - blank posts as 0, matching the prior
// Number("") behavior. In BRUTTO mode "price" holds what the user typed
// (VAT-inclusive); the database always stores netto (see the component's
// derivedPrice comment), so it's converted back here.
function buildItemPayload(data, priceEntryMode) {
  const enteredPrice = parseDecimal(data.price) ?? 0;
  const pdv = parseDecimal(data.pdv) ?? 0;
  const price =
    priceEntryMode === "BRUTTO"
      ? Number((enteredPrice / (1 + pdv / 100)).toFixed(2))
      : enteredPrice;

  return {
    title: data.title.trim(),
    description: data?.description?.trim() ?? "",
    categoryName: data?.category?.trim(),
    supplierName: data.supplier?.trim(),
    price,
    pdv,
    quantity: Number(data.quantity),
    code: data.code?.trim(),
    serialNumber: data.serialNumber?.trim(),
    measuringUnit: data.measuringUnit?.trim(),
    discount: parseDecimal(data.discount) ?? 0,
    antiRabiesVaccine: Boolean(data.antiRabiesVaccine),
    otherVaccine: Boolean(data.otherVaccine),
    narcoticDrug: Boolean(data.narcoticDrug),
    prescriptionRequired: Boolean(data.prescriptionRequired),
    sellable: Boolean(data?.sellable),
    expirationDate: data.expirationDate
      ? dayjs(data.expirationDate).format("YYYY-MM-DD")
      : null,
  };
}

export default function WarehouseItemForm({
  categories,
  suppliers,
  initialValues,
  onSubmit,
  onCancel,
}) {
  const { t } = useTranslation();
  const {id} = useParams();
  const {roles, currentClinic} = useUser();
  const clinicId = roles.master ? id : currentClinic.id;
  const queryClient = useQueryClient();
  const isEditMode = Boolean(initialValues);

  const submission = useSubmit(null, WAREHOUSE_ITEM_SUBMIT + clinicId, WAREHOUSE_ITEMS_SUBMIT, [
    WAREHOUSE_CATEGORIES,
    WAREHOUSE_SUPPLIERS,
    WAREHOUSE_ITEMS,
  ])

  const { update, isUpdating, getLastError: getUpdateError } = useUpdateWarehouseItem(clinicId);
  const { data: warehousePreferences } = useGetWarehousePreferences(clinicId);
  const priceEntryMode = warehousePreferences?.priceEntryMode ?? "NETTO";
  const defaultVat = warehousePreferences?.defaultVat;
  const currency = warehousePreferences?.currency ?? "EUR";

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    getValues,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      ...emptyValues,
      ...(initialValues ?? {}),
      antiRabiesVaccine: Boolean(initialValues?.antiRabiesVaccine),
      otherVaccine: Boolean(initialValues?.otherVaccine),
      sellable: Boolean(initialValues?.sellable),
      narcoticDrug: Boolean(initialValues?.narcoticDrug),
      prescriptionRequired: Boolean(initialValues?.prescriptionRequired),
      expirationDate: initialValues?.expirationDate
        ? dayjs(initialValues.expirationDate)
        : null,
    },
  });

  const descriptionValue = watch("description") ?? "";
  const priceValue = watch("price");
  const pdvValue = watch("pdv");

  // In BRUTTO mode, "price" holds what the user typed - the VAT-inclusive
  // value - and the derived box below shows the netto price instead. The
  // database always stores netto (see submitData), so this is purely a
  // form-entry convenience; nothing downstream needs to know which mode
  // was used to enter it.
  const derivedPrice = React.useMemo(() => {
    const price = parseDecimal(priceValue);
    const pdv = parseDecimal(pdvValue);
    if (price == null || Number.isNaN(price) || pdv == null || Number.isNaN(pdv)) {
      return null;
    }
    return priceEntryMode === "BRUTTO"
      ? price / (1 + pdv / 100)
      : price + (price * pdv) / 100;
  }, [priceValue, pdvValue, priceEntryMode]);

  // Default VAT: create-mode only (never silently rewrite a saved item's
  // VAT on edit), and only once a price has actually been entered and the
  // VAT field is still blank/untouched - never override a value the user
  // already set.
  React.useEffect(() => {
    if (isEditMode || defaultVat == null) {
      return;
    }
    const price = parseDecimal(priceValue);
    if (price == null || Number.isNaN(price)) {
      return;
    }
    if (getValues("pdv") === "") {
      setValue("pdv", String(defaultVat));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceValue, isEditMode, defaultVat]);

  // Editing an existing item in BRUTTO mode: the stored price is netto, so
  // seed the field with the VAT-inclusive value once the clinic preference
  // has loaded. Guarded to run only once so it doesn't fight the user's
  // own edits on every re-render.
  const brutSeedApplied = React.useRef(false);
  React.useEffect(() => {
    if (!isEditMode || brutSeedApplied.current || priceEntryMode !== "BRUTTO" || !warehousePreferences) {
      return;
    }
    const netto = parseDecimal(initialValues?.price);
    const pdv = parseDecimal(initialValues?.pdv);
    if (netto == null || Number.isNaN(netto) || pdv == null || Number.isNaN(pdv)) {
      return;
    }
    setValue("price", (netto * (1 + pdv / 100)).toFixed(2));
    brutSeedApplied.current = true;
  }, [isEditMode, priceEntryMode, warehousePreferences, initialValues, setValue]);

  const submitData = async (data) => {
    const payload = buildItemPayload(data, priceEntryMode);

    if (isEditMode) {
      const updatedItem = await update(initialValues.id, payload);

      if (updatedItem) {
        queryClient.invalidateQueries({ queryKey: [WAREHOUSE_ITEMS] });
        onSubmit(updatedItem);
      } else if (getUpdateError() === "WarehouseItemCodeAlreadyExists") {
        setError("code", {
          type: "server",
          message: t("WarehouseItemCodeAlreadyExists"),
        });
      }
      return;
    }

    const success = await submission.submitCallback(payload);
    if (success) {
      queryClient.invalidateQueries({ queryKey: [WAREHOUSE_ITEMS] });
      onSubmit(payload);
    } else if (submission.getLastError() === "WarehouseItemCodeAlreadyExists") {
      setError("code", {
        type: "server",
        message: t("WarehouseItemCodeAlreadyExists"),
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <form onSubmit={handleSubmit(submitData)}>
        <MUI.Stack spacing={3}>
          <MUI.Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t(isEditMode ? "EditWarehouseItem" : "AddWarehouseItem")}
          </MUI.Typography>

          <SectionCard label={t("BasicInformation")}>
            <MUI.Stack spacing={2.5}>
              <Controller
                control={control}
                name="title"
                rules={{
                  required: t("EmptyField"),
                  maxLength: {
                    value: TITLE_MAX_LENGTH,
                    message: t("MaxCharacters") + `: ${TITLE_MAX_LENGTH}`,
                  },
                }}
                render={({ field }) => (
                  <MUI.TextField
                    {...field}
                    label={mandatory(t("Title"))}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: TITLE_MAX_LENGTH }}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="description"
                rules={{
                  maxLength: {
                    value: DESCRIPTION_MAX_LENGTH,
                    message: t("MaxCharacters") + `: ${DESCRIPTION_MAX_LENGTH}`,
                  },
                }}
                render={({ field }) => (
                  <MUI.TextField
                    {...field}
                    label={t("Description")}
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                    error={!!errors.description}
                    helperText={
                      errors.description?.message ??
                      `${descriptionValue.length}/${DESCRIPTION_MAX_LENGTH}`
                    }
                  />
                )}
              />
            </MUI.Stack>
          </SectionCard>

          <SectionCard label={t("Classification")}>
            <MUI.Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2.5,
              }}
            >
              <MUI.Box sx={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="category"
                  rules={{
                    maxLength: {
                      value: CATEGORY_MAX_LENGTH,
                      message: t("MaxCharacters") + `: ${CATEGORY_MAX_LENGTH}`,
                    },
                  }}
                  render={({ field }) => (
                    <SearchableAutocomplete
                      options={categories}
                      value={field.value}
                      onChange={field.onChange}
                      label="Category"
                      required={false}
                      error={!!errors.category}
                      helperText={errors.category?.message}
                      maxLength={CATEGORY_MAX_LENGTH}
                    />
                  )}
                />
              </MUI.Box>
              <MUI.Box sx={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="supplier"
                  rules={{
                    maxLength: {
                      value: SUPPLIER_MAX_LENGTH,
                      message: t("MaxCharacters") + `: ${SUPPLIER_MAX_LENGTH}`,
                    },
                  }}
                  render={({ field }) => (
                    <SearchableAutocomplete
                      options={suppliers}
                      value={field.value}
                      onChange={field.onChange}
                      label="Supplier"
                      required={false}
                      error={!!errors.supplier}
                      helperText={errors.supplier?.message}
                      maxLength={SUPPLIER_MAX_LENGTH}
                    />
                  )}
                />
              </MUI.Box>
            </MUI.Box>
          </SectionCard>

          <SectionCard label={t("InventoryDetails")}>
            <MUI.Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                rowGap: 2.5,
                columnGap: 2.5,
              }}
            >
              <Controller
                control={control}
                name="code"
                rules={{
                  maxLength: {
                    value: CODE_MAX_LENGTH,
                    message: t("MaxCharacters") + `: ${CODE_MAX_LENGTH}`,
                  },
                }}
                render={({ field }) => (
                  <MUI.TextField
                    {...field}
                    label={t("Code")}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: CODE_MAX_LENGTH }}
                    error={!!errors.code}
                    helperText={errors.code?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="serialNumber"
                rules={{
                  maxLength: {
                    value: SERIAL_NUMBER_MAX_LENGTH,
                    message: t("MaxCharacters") + `: ${SERIAL_NUMBER_MAX_LENGTH}`,
                  },
                }}
                render={({ field }) => (
                  <MUI.TextField
                    {...field}
                    label={t("SerialNumber")}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: SERIAL_NUMBER_MAX_LENGTH }}
                    error={!!errors.serialNumber}
                    helperText={errors.serialNumber?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="measuringUnit"
                render={({ field }) => (
                  <MUI.Autocomplete
                    options={MEASURING_UNITS.map((unit) => unit.key)}
                    value={field.value || null}
                    onChange={(event, newValue) => field.onChange(newValue ?? "")}
                    onBlur={field.onBlur}
                    getOptionLabel={(key) => {
                      const unit = MEASURING_UNITS.find((u) => u.key === key);
                      return unit ? t(unit.labelKey) : key;
                    }}
                    isOptionEqualToValue={(option, val) => option === val}
                    size="small"
                    fullWidth
                    renderInput={(params) => (
                      <MUI.TextField
                        {...params}
                        label={t("MeasuringUnit")}
                        error={!!errors.measuringUnit}
                        helperText={errors.measuringUnit?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                control={control}
                name="expirationDate"
                render={({ field: { value, onChange } }) => (
                  <MUI.FormControl fullWidth>
                    <MobileDatePicker
                      value={value ?? null}
                      onChange={onChange}
                      label={t("ExpirationDate")}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: { size: "small" },
                        actionBar: { actions: ["clear", "cancel", "accept"] },
                      }}
                    />
                  </MUI.FormControl>
                )}
              />
            </MUI.Box>
          </SectionCard>

          <SectionCard label={t("PricingAndStock")}>
            <MUI.Stack spacing={2.5}>
              <MUI.Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2.5,
                }}
              >
                <Controller
                  control={control}
                  name="price"
                  rules={decimalRules(t, { min: 0 })}
                  render={({ field }) => (
                    <MUI.TextField
                      {...field}
                      type="text"
                      inputMode="decimal"
                      label={t(priceEntryMode === "BRUTTO" ? "PriceWithVat" : "Price")}
                      fullWidth
                      size="small"
                      error={!!errors.price}
                      helperText={
                        errors.price?.message ??
                        t(priceEntryMode === "BRUTTO" ? "PriceIncludesVat" : "PriceExcludesVat")
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="pdv"
                  rules={decimalRules(t, { min: 0, max: 100 })}
                  render={({ field }) => (
                    <MUI.TextField
                      {...field}
                      type="text"
                      inputMode="decimal"
                      label={t("PDV")}
                      fullWidth
                      size="small"
                      error={!!errors.pdv}
                      helperText={errors.pdv?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="discount"
                  rules={decimalRules(t, { min: 0, max: 100 })}
                  render={({ field }) => (
                    <MUI.TextField
                      {...field}
                      type="text"
                      inputMode="decimal"
                      label={t("Discount")}
                      fullWidth
                      size="small"
                      error={!!errors.discount}
                      helperText={errors.discount?.message}
                    />
                  )}
                />
              </MUI.Box>

              {derivedPrice !== null && (
                <MUI.Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2.5,
                    bgcolor: (theme) => MUI.alpha(theme.palette.primary.main, 0.08),
                    border: "1px solid",
                    borderColor: (theme) => MUI.alpha(theme.palette.primary.main, 0.24),
                  }}
                >
                  <MUI.Typography variant="caption" color="text.secondary">
                    {t(priceEntryMode === "BRUTTO" ? "Price" : "PriceWithVat")}
                  </MUI.Typography>
                  <MUI.Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                    {formatCurrency(derivedPrice, currency)}
                  </MUI.Typography>
                </MUI.Box>
              )}

              <MUI.Divider />

              <Controller
                control={control}
                name="quantity"
                rules={{
                  min: { value: 0, message: t("MustBePositiveNumber") },
                }}
                render={({ field }) => {
                  const currentValue = Number(field.value) || 0;
                  const adjust = (delta) => {
                    field.onChange(Math.max(0, currentValue + delta));
                  };

                  return (
                    <MUI.Stack spacing={1.25}>
                      <MUI.Typography variant="caption" color="text.secondary">
                        {t("Quantity")}
                      </MUI.Typography>
                      <MUI.Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {QUANTITY_STEPS.filter((step) => step < 0).map((step) => (
                          <MUI.Button
                            key={step}
                            variant="outlined"
                            size="medium"
                            onClick={() => adjust(step)}
                            disabled={currentValue <= 0}
                            sx={{ borderRadius: 2, minWidth: 56 }}
                          >
                            {step}
                          </MUI.Button>
                        ))}
                        <MUI.TextField
                          {...field}
                          type="number"
                          size="small"
                          sx={{ width: 140 }}
                          inputProps={{ min: 0, step: "1" }}
                          error={!!errors.quantity}
                          helperText={errors.quantity?.message}
                        />
                        {QUANTITY_STEPS.filter((step) => step > 0).map((step) => (
                          <MUI.Button
                            key={step}
                            variant="outlined"
                            size="medium"
                            onClick={() => adjust(step)}
                            sx={{ borderRadius: 2, minWidth: 56 }}
                          >
                            +{step}
                          </MUI.Button>
                        ))}
                      </MUI.Stack>
                    </MUI.Stack>
                  );
                }}
              />
            </MUI.Stack>
          </SectionCard>

          <SectionCard label={t("AdditionalInformation")}>
            <MUI.Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              }}
            >
              <Controller
                control={control}
                name="antiRabiesVaccine"
                render={({ field }) => (
                  <MUI.FormControlLabel
                    control={<MUI.Checkbox {...field} checked={Boolean(field.value)} />}
                    label={t("AntiRabiesVaccine")}
                  />
                )}
              />
              <Controller
                control={control}
                name="otherVaccine"
                render={({ field }) => (
                  <MUI.FormControlLabel
                    control={<MUI.Checkbox {...field} checked={Boolean(field.value)} />}
                    label={t("OtherVaccine")}
                  />
                )}
              />
              <Controller
                control={control}
                name="narcoticDrug"
                render={({ field }) => (
                  <MUI.FormControlLabel
                    control={<MUI.Checkbox {...field} checked={Boolean(field.value)} />}
                    label={t("NarcoticDrug")}
                  />
                )}
              />
              <Controller
                control={control}
                name="prescriptionRequired"
                render={({ field }) => (
                  <MUI.FormControlLabel
                    control={<MUI.Checkbox {...field} checked={Boolean(field.value)} />}
                    label={t("PrescriptionRequired")}
                  />
                )}
              />
               <Controller
                control={control}
                name="sellable"
                render={({ field }) => (
                  <MUI.FormControlLabel
                    control={<MUI.Checkbox {...field} checked={Boolean(field.value)} />}
                    label={t("Sellable")}
                  />
                )}
              />
            </MUI.Box>
          </SectionCard>

          <MUI.Stack spacing={2}>
            <MUI.Typography variant="caption" color="error">
              {`* ${t("RequiredFields")}`}
            </MUI.Typography>
            <ConfirmAndCancelButtons
              confirmTitle="Save"
              cancelTitle=""
              onCancel={onCancel}
              confirmButtonType="submit"
              isConfirmLoading={isEditMode ? isUpdating : submission.loading}
            />
          </MUI.Stack>
        </MUI.Stack>
      </form>
    </LocalizationProvider>
  );
}
